import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './HomePage.css';
import ShirtIcon from '../components/ShirtIcon';
import NavIcon from '../components/shared/NavIcons';
import AdSense, { ADSENSE_SLOTS } from '../components/ads/AdSense';
import SurfsharkAffiliate from '../components/affiliate/SurfsharkAffiliate';
import trackEvent from '../utils/analytics';
import { HOMEPAGE_CALENDAR_EVENTS } from '../data/homepageFootballCalendar';
import {
  DEFAULT_HOMEPAGE_FLAGSHIP_ID,
  selectHomepageFlagship,
} from '../utils/homepageFlagship';

// ─── SVG card visuals ─────────────────────────────────────────────────────────

function TransferVisual() {
  return (
    <svg viewBox="0 0 200 120" fill="none" aria-hidden="true">
      <rect width="200" height="120" fill="rgba(110,232,79,0.03)" />
      <rect x="28" y="12" width="144" height="96" rx="4" stroke="rgba(110,232,79,0.16)" strokeWidth="1" fill="none" />
      <line x1="100" y1="12" x2="100" y2="108" stroke="rgba(110,232,79,0.10)" strokeWidth="1" />
      <circle cx="56" cy="60" r="11" fill="rgba(110,232,79,0.12)" stroke="#6ee84f" strokeWidth="1.5" />
      <circle cx="144" cy="60" r="11" fill="rgba(110,232,79,0.12)" stroke="#6ee84f" strokeWidth="1.5" />
      <circle cx="56" cy="36" r="7" fill="rgba(110,232,79,0.08)" stroke="rgba(110,232,79,0.45)" strokeWidth="1" />
      <circle cx="144" cy="84" r="7" fill="rgba(110,232,79,0.08)" stroke="rgba(110,232,79,0.45)" strokeWidth="1" />
      <defs>
        <marker id="ta1" markerWidth="5" markerHeight="5" refX="2.5" refY="2.5" orient="auto">
          <path d="M0,0 L5,2.5 L0,5 Z" fill="#6ee84f" />
        </marker>
        <marker id="ta2" markerWidth="5" markerHeight="5" refX="2.5" refY="2.5" orient="auto">
          <path d="M0,0 L5,2.5 L0,5 Z" fill="#6ee84f" />
        </marker>
      </defs>
      <path d="M72 53 L128 42" stroke="#6ee84f" strokeWidth="1.8" strokeDasharray="4 2.5" markerEnd="url(#ta1)" />
      <path d="M128 74 L72 70" stroke="#6ee84f" strokeWidth="1.8" strokeDasharray="4 2.5" markerEnd="url(#ta2)" />
      <text x="100" y="65" fill="rgba(110,232,79,0.28)" fontSize="15" fontWeight="900" textAnchor="middle" fontFamily="Montserrat,sans-serif">£</text>
    </svg>
  );
}

function BudgetSquadVisual() {
  return (
    <svg viewBox="0 0 200 120" fill="none" aria-hidden="true">
      <rect width="200" height="120" fill="rgba(250,204,21,0.04)" />
      <rect x="60" y="10" width="80" height="100" rx="6" stroke="rgba(250,204,21,0.22)" strokeWidth="1" fill="none" />
      <line x1="60" y1="60" x2="140" y2="60" stroke="rgba(250,204,21,0.16)" strokeWidth="1" />
      <circle cx="100" cy="60" r="14" stroke="rgba(250,204,21,0.22)" strokeWidth="1" fill="none" />
      <circle cx="80" cy="30" r="7" fill="rgba(250,204,21,0.12)" stroke="#facc15" strokeWidth="1.4" />
      <circle cx="120" cy="30" r="7" fill="rgba(250,204,21,0.12)" stroke="#facc15" strokeWidth="1.4" />
      <circle cx="100" cy="90" r="7" fill="rgba(250,204,21,0.12)" stroke="#facc15" strokeWidth="1.4" />
      <rect x="18" y="44" width="36" height="22" rx="6" fill="rgba(56,189,248,0.12)" stroke="#38bdf8" strokeWidth="1.4" />
      <text x="36" y="60" fill="#38bdf8" fontSize="11" fontWeight="900" textAnchor="middle" fontFamily="Montserrat,sans-serif">£</text>
      <rect x="146" y="44" width="36" height="22" rx="6" fill="rgba(34,197,122,0.12)" stroke="#22c77a" strokeWidth="1.4" />
      <text x="164" y="61" fill="#22c77a" fontSize="9" fontWeight="900" textAnchor="middle" fontFamily="Montserrat,sans-serif">XI</text>
    </svg>
  );
}

function LineupVisual() {
  const dots = [
    [100, 104],
    [44, 82], [72, 82], [128, 82], [156, 82],
    [56, 56], [100, 54], [144, 56],
    [58, 28], [100, 24], [142, 28],
  ];
  return (
    <svg viewBox="0 0 200 120" fill="none" aria-hidden="true">
      <rect width="200" height="120" fill="rgba(56,189,248,0.03)" />
      <rect x="28" y="8" width="144" height="104" rx="4" stroke="rgba(56,189,248,0.18)" strokeWidth="1" fill="none" />
      <line x1="100" y1="8" x2="100" y2="112" stroke="rgba(56,189,248,0.09)" strokeWidth="1" />
      <ellipse cx="100" cy="60" rx="18" ry="14" stroke="rgba(56,189,248,0.12)" strokeWidth="1" fill="none" />
      {dots.map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r={i === 0 ? 8 : 6.5}
          fill="rgba(56,189,248,0.15)" stroke="#38bdf8" strokeWidth="1.5" />
      ))}
    </svg>
  );
}

function TacticsVisual() {
  return (
    <svg viewBox="0 0 200 120" fill="none" aria-hidden="true">
      <rect width="200" height="120" fill="rgba(250,204,21,0.03)" />
      <rect x="28" y="8" width="144" height="104" rx="4" stroke="rgba(250,204,21,0.16)" strokeWidth="1" fill="none" />
      <circle cx="100" cy="96" r="8"  fill="rgba(250,204,21,0.13)" stroke="#facc15" strokeWidth="1.4" />
      <circle cx="58"  cy="72" r="7"  fill="rgba(250,204,21,0.10)" stroke="#facc15" strokeWidth="1.2" />
      <circle cx="142" cy="72" r="7"  fill="rgba(250,204,21,0.10)" stroke="#facc15" strokeWidth="1.2" />
      <circle cx="76"  cy="46" r="7"  fill="rgba(250,204,21,0.10)" stroke="#facc15" strokeWidth="1.2" />
      <circle cx="124" cy="46" r="7"  fill="rgba(250,204,21,0.10)" stroke="#facc15" strokeWidth="1.2" />
      <circle cx="100" cy="22" r="8"  fill="rgba(250,204,21,0.13)" stroke="#facc15" strokeWidth="1.4" />
      <defs>
        <marker id="yarr" markerWidth="5" markerHeight="5" refX="2.5" refY="2.5" orient="auto">
          <path d="M0,0 L5,2.5 L0,5 Z" fill="#facc15" />
        </marker>
      </defs>
      <path d="M100 88 L80 54" stroke="#facc15" strokeWidth="1.4" markerEnd="url(#yarr)" opacity="0.65" />
      <path d="M58 65 L76 52" stroke="#facc15" strokeWidth="1.4" markerEnd="url(#yarr)" opacity="0.65" />
      <path d="M142 65 L124 52" stroke="#facc15" strokeWidth="1.4" markerEnd="url(#yarr)" opacity="0.65" />
      <text x="100" y="20" fill="rgba(250,204,21,0.55)" fontSize="7" fontFamily="sans-serif" textAnchor="middle">✕</text>
      <text x="76"  y="44" fill="rgba(250,204,21,0.55)" fontSize="7" fontFamily="sans-serif" textAnchor="middle">✕</text>
    </svg>
  );
}

function DraftVisual({ heading = 'PREMIER DRAFT', record = '38-0', caption = 'SEASON UNBEATEN' }) {
  return (
    <svg viewBox="0 0 200 120" fill="none" aria-hidden="true">
      <rect width="200" height="120" fill="rgba(46,196,182,0.04)" />
      <rect x="22" y="14" width="156" height="92" rx="9" fill="rgba(46,196,182,0.07)" stroke="rgba(46,196,182,0.28)" strokeWidth="1.5" />
      <text x="100" y="38" fill="rgba(46,196,182,0.38)" fontSize="8" textAnchor="middle" fontFamily="Montserrat,sans-serif" fontWeight="700" letterSpacing="1.5">{heading}</text>
      <text x="100" y="78" fill="#2ec4b6" fontSize="38" fontWeight="900" textAnchor="middle" fontFamily="Montserrat,sans-serif" letterSpacing="1">{record}</text>
      <text x="100" y="97" fill="rgba(46,196,182,0.5)" fontSize="8" textAnchor="middle" fontFamily="Montserrat,sans-serif" fontWeight="700" letterSpacing="1.5">{caption}</text>
      <circle cx="40" cy="32" r="4" fill="rgba(46,196,182,0.28)" />
      <circle cx="160" cy="32" r="4" fill="rgba(46,196,182,0.28)" />
    </svg>
  );
}

function ChampionsXiVisual() {
  return <DraftVisual heading="EUROPEAN DRAFT" record="15-0" caption="PERFECT CAMPAIGN" />;
}

function ImposterVisual() {
  return (
    <svg viewBox="0 0 200 120" fill="none" aria-hidden="true">
      <rect width="200" height="120" fill="rgba(246,195,67,0.04)" />
      <rect x="24" y="14" width="152" height="92" rx="8" fill="rgba(15,23,42,0.45)" stroke="rgba(246,195,67,0.28)" strokeWidth="1.5" />
      <circle cx="72" cy="58" r="19" fill="rgba(110,232,79,0.12)" stroke="#6ee84f" strokeWidth="1.5" />
      <circle cx="128" cy="58" r="19" fill="rgba(246,195,67,0.13)" stroke="#f6c343" strokeWidth="1.5" strokeDasharray="4 3" />
      <path d="M63 60c3 6 15 6 18 0" stroke="#6ee84f" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M119 60c3 6 15 6 18 0" stroke="#f6c343" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="65" cy="53" r="2" fill="#6ee84f" />
      <circle cx="79" cy="53" r="2" fill="#6ee84f" />
      <circle cx="121" cy="53" r="2" fill="#f6c343" />
      <circle cx="135" cy="53" r="2" fill="#f6c343" />
      <text x="100" y="94" fill="rgba(246,195,67,0.72)" fontSize="9" textAnchor="middle" fontFamily="Montserrat,sans-serif" fontWeight="800" letterSpacing="1.3">WHO KNOWS?</text>
    </svg>
  );
}

function AlphabetVisual() {
  return (
    <svg viewBox="0 0 200 120" fill="none" aria-hidden="true">
      <rect width="200" height="120" fill="rgba(110,232,79,0.04)" />
      <rect x="24" y="14" width="152" height="92" rx="8" fill="rgba(15,23,42,0.45)" stroke="rgba(110,232,79,0.28)" strokeWidth="1.5" />
      <text x="62" y="74" fill="#6ee84f" fontSize="42" textAnchor="middle" fontFamily="Montserrat,sans-serif" fontWeight="900">A</text>
      <text x="138" y="74" fill="#f6c343" fontSize="42" textAnchor="middle" fontFamily="Montserrat,sans-serif" fontWeight="900">Z</text>
      <path d="M84 64h32" stroke="rgba(248,250,252,0.4)" strokeWidth="2" strokeDasharray="3 4" strokeLinecap="round" />
      <circle cx="100" cy="64" r="6" fill="rgba(248,250,252,0.85)" />
      <text x="100" y="94" fill="rgba(110,232,79,0.72)" fontSize="9" textAnchor="middle" fontFamily="Montserrat,sans-serif" fontWeight="800" letterSpacing="1.3">A TO Z</text>
    </svg>
  );
}

function CardClashVisual() {
  return (
    <svg viewBox="0 0 200 120" fill="none" aria-hidden="true">
      <rect width="200" height="120" fill="rgba(56,189,248,0.04)" />
      <rect x="22" y="20" width="62" height="84" rx="8" fill="rgba(15,23,42,0.5)" stroke="#19b46b" strokeWidth="1.5" />
      <rect x="116" y="20" width="62" height="84" rx="8" fill="rgba(15,23,42,0.5)" stroke="#38bdf8" strokeWidth="1.5" strokeDasharray="4 3" />
      <text x="53" y="58" fill="#19b46b" fontSize="20" textAnchor="middle" fontFamily="Montserrat,sans-serif" fontWeight="900">91</text>
      <text x="147" y="58" fill="#38bdf8" fontSize="20" textAnchor="middle" fontFamily="Montserrat,sans-serif" fontWeight="900">88</text>
      <rect x="34" y="68" width="38" height="8" rx="3" fill="rgba(25,180,107,0.25)" />
      <rect x="128" y="68" width="38" height="8" rx="3" fill="rgba(56,189,248,0.2)" />
      <text x="100" y="62" fill="rgba(248,250,252,0.55)" fontSize="11" textAnchor="middle" fontFamily="Montserrat,sans-serif" fontWeight="900">VS</text>
      <text x="100" y="94" fill="rgba(56,189,248,0.72)" fontSize="9" textAnchor="middle" fontFamily="Montserrat,sans-serif" fontWeight="800" letterSpacing="1.3">CARD CLASH</text>
    </svg>
  );
}

function AuctionDraftVisual() {
  return (
    <svg viewBox="0 0 200 120" fill="none" aria-hidden="true">
      <rect width="200" height="120" fill="rgba(250,204,21,0.04)" />
      <rect x="18" y="18" width="164" height="84" rx="9" fill="rgba(15,23,42,0.52)" stroke="rgba(250,204,21,0.34)" strokeWidth="1.5" />
      <circle cx="57" cy="49" r="19" fill="rgba(34,197,122,0.16)" stroke="#22c77a" strokeWidth="1.4" />
      <text x="57" y="56" fill="#86efac" fontSize="17" textAnchor="middle" fontFamily="Montserrat,sans-serif" fontWeight="900">£</text>
      <path d="M96 40h61M96 55h46M96 70h52" stroke="rgba(56,189,248,0.64)" strokeWidth="5" strokeLinecap="round" />
      <path d="M38 84h124" stroke="rgba(250,204,21,0.3)" strokeWidth="2" />
      <circle cx="49" cy="84" r="4" fill="#38bdf8" />
      <circle cx="74" cy="84" r="4" fill="#22c77a" />
      <circle cx="99" cy="84" r="4" fill="#facc15" />
      <text x="100" y="96" fill="rgba(250,204,21,0.8)" fontSize="9" textAnchor="middle" fontFamily="Montserrat,sans-serif" fontWeight="800" letterSpacing="1.3">AUCTION DRAFT</text>
    </svg>
  );
}

function BuyKeepSellVisual() {
  return (
    <svg viewBox="0 0 200 120" fill="none" aria-hidden="true">
      <rect width="200" height="120" fill="rgba(168,85,247,0.04)" />
      <rect x="14" y="20" width="50" height="80" rx="7" fill="rgba(34,197,122,0.08)" stroke="#22c77a" strokeWidth="1.4" />
      <rect x="75" y="20" width="50" height="80" rx="7" fill="rgba(56,189,248,0.08)" stroke="#38bdf8" strokeWidth="1.4" strokeDasharray="4 3" />
      <rect x="136" y="20" width="50" height="80" rx="7" fill="rgba(251,146,60,0.08)" stroke="#fb923c" strokeWidth="1.4" />
      <text x="39" y="46" fill="#22c77a" fontSize="11" fontWeight="900" textAnchor="middle" fontFamily="Montserrat,sans-serif">BUY</text>
      <text x="100" y="46" fill="#38bdf8" fontSize="11" fontWeight="900" textAnchor="middle" fontFamily="Montserrat,sans-serif">KEEP</text>
      <text x="161" y="46" fill="#fb923c" fontSize="11" fontWeight="900" textAnchor="middle" fontFamily="Montserrat,sans-serif">SELL</text>
      <rect x="22" y="58" width="34" height="7" rx="3" fill="rgba(34,197,122,0.3)" />
      <rect x="22" y="70" width="34" height="7" rx="3" fill="rgba(34,197,122,0.18)" />
      <rect x="83" y="58" width="34" height="7" rx="3" fill="rgba(56,189,248,0.3)" />
      <rect x="83" y="70" width="34" height="7" rx="3" fill="rgba(56,189,248,0.18)" />
      <rect x="83" y="82" width="34" height="7" rx="3" fill="rgba(56,189,248,0.12)" />
      <rect x="144" y="58" width="34" height="7" rx="3" fill="rgba(251,146,60,0.3)" />
    </svg>
  );
}

function StartBenchSellVisual() {
  return (
    <svg viewBox="0 0 200 120" fill="none" aria-hidden="true">
      <rect width="200" height="120" fill="rgba(34,197,122,0.04)" />
      <rect x="14" y="20" width="50" height="80" rx="7" fill="rgba(34,197,122,0.1)" stroke="#22c77a" strokeWidth="1.4" />
      <rect x="75" y="20" width="50" height="80" rx="7" fill="rgba(250,204,21,0.1)" stroke="#facc15" strokeWidth="1.4" strokeDasharray="4 3" />
      <rect x="136" y="20" width="50" height="80" rx="7" fill="rgba(239,68,68,0.1)" stroke="#ef4444" strokeWidth="1.4" />
      <text x="39" y="46" fill="#22c77a" fontSize="10" fontWeight="900" textAnchor="middle" fontFamily="Montserrat,sans-serif">START</text>
      <text x="100" y="46" fill="#facc15" fontSize="10" fontWeight="900" textAnchor="middle" fontFamily="Montserrat,sans-serif">BENCH</text>
      <text x="161" y="46" fill="#ef4444" fontSize="10" fontWeight="900" textAnchor="middle" fontFamily="Montserrat,sans-serif">SELL</text>
      <rect x="22" y="58" width="34" height="7" rx="3" fill="rgba(34,197,122,0.3)" />
      <rect x="83" y="58" width="34" height="7" rx="3" fill="rgba(250,204,21,0.3)" />
      <rect x="144" y="58" width="34" height="7" rx="3" fill="rgba(239,68,68,0.3)" />
      <rect x="22" y="70" width="34" height="7" rx="3" fill="rgba(34,197,122,0.16)" />
      <rect x="83" y="70" width="34" height="7" rx="3" fill="rgba(250,204,21,0.16)" />
      <rect x="144" y="70" width="34" height="7" rx="3" fill="rgba(239,68,68,0.16)" />
    </svg>
  );
}

function TierListVisual() {
  return (
    <svg viewBox="0 0 200 120" fill="none" aria-hidden="true">
      <rect width="200" height="120" fill="rgba(250,204,21,0.04)" />
      <rect x="14" y="14" width="172" height="15" rx="5" fill="#dc2626" />
      <text x="100" y="25" fill="#fff" fontSize="10" fontWeight="900" textAnchor="middle" fontFamily="Montserrat,sans-serif">S</text>
      <rect x="14" y="33" width="172" height="15" rx="5" fill="#ea580c" />
      <text x="100" y="44" fill="#fff" fontSize="10" fontWeight="900" textAnchor="middle" fontFamily="Montserrat,sans-serif">A</text>
      <rect x="14" y="52" width="172" height="15" rx="5" fill="#b45309" />
      <text x="100" y="63" fill="#fff" fontSize="10" fontWeight="900" textAnchor="middle" fontFamily="Montserrat,sans-serif">B</text>
      <rect x="14" y="71" width="172" height="15" rx="5" fill="#16a34a" />
      <text x="100" y="82" fill="#fff" fontSize="10" fontWeight="900" textAnchor="middle" fontFamily="Montserrat,sans-serif">C</text>
      <rect x="14" y="90" width="172" height="15" rx="5" fill="#1d4ed8" />
      <text x="100" y="101" fill="#fff" fontSize="10" fontWeight="900" textAnchor="middle" fontFamily="Montserrat,sans-serif">D</text>
    </svg>
  );
}

function SquadBuilderVisual() {
  return (
    <svg viewBox="0 0 200 120" fill="none" aria-hidden="true">
      <rect width="200" height="120" fill="rgba(250,204,21,0.04)" />
      <rect x="22" y="18" width="156" height="86" rx="9" fill="rgba(15,23,42,0.5)" stroke="rgba(250,204,21,0.3)" strokeWidth="1.5" />
      <rect x="34" y="30" width="36" height="50" rx="6" fill="rgba(56,189,248,0.1)" stroke="#38bdf8" strokeWidth="1.4" />
      <rect x="82" y="30" width="36" height="50" rx="6" fill="rgba(250,204,21,0.14)" stroke="#facc15" strokeWidth="1.4" />
      <rect x="130" y="30" width="36" height="50" rx="6" fill="rgba(56,189,248,0.1)" stroke="#38bdf8" strokeWidth="1.4" />
      <text x="100" y="60" fill="rgba(248,250,252,0.55)" fontSize="10" textAnchor="middle" fontFamily="Montserrat,sans-serif" fontWeight="900">PICK</text>
      <text x="100" y="96" fill="rgba(250,204,21,0.75)" fontSize="9" textAnchor="middle" fontFamily="Montserrat,sans-serif" fontWeight="800" letterSpacing="1.3">SQUAD BUILDER</text>
    </svg>
  );
}

function WorldXIFeaturedVisual() {
  return (
    <svg viewBox="0 0 400 200" fill="none" aria-hidden="true">
      <defs>
        <radialGradient id="fvGlow" cx="60%" cy="50%" r="55%">
          <stop offset="0%" stopColor="rgba(251,191,36,0.12)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>
      <rect width="400" height="200" fill="url(#fvGlow)" />
      {/* Globe */}
      <circle cx="100" cy="100" r="72" stroke="rgba(110,232,79,0.22)" strokeWidth="1.5" fill="rgba(110,232,79,0.05)" />
      <ellipse cx="100" cy="100" rx="36" ry="72" stroke="rgba(110,232,79,0.12)" strokeWidth="1" fill="none" />
      <line x1="28" y1="100" x2="172" y2="100" stroke="rgba(110,232,79,0.12)" strokeWidth="1" />
      <line x1="38" y1="72" x2="162" y2="72" stroke="rgba(110,232,79,0.07)" strokeWidth="0.8" />
      <line x1="38" y1="128" x2="162" y2="128" stroke="rgba(110,232,79,0.07)" strokeWidth="0.8" />
      {/* Stars */}
      <text x="258" y="34" fill="rgba(250,204,21,0.9)" fontSize="13" textAnchor="middle" letterSpacing="8" fontFamily="sans-serif">★★★</text>
      {/* Trophy */}
      <text x="258" y="136" fill="#fbbf24" fontSize="84" textAnchor="middle" fontFamily="serif">🏆</text>
      {/* Tournament bracket */}
      <line x1="334" y1="68"  x2="360" y2="68"  stroke="rgba(110,232,79,0.28)" strokeWidth="1.5" />
      <line x1="334" y1="94"  x2="360" y2="94"  stroke="rgba(110,232,79,0.28)" strokeWidth="1.5" />
      <line x1="360" y1="68"  x2="360" y2="94"  stroke="rgba(110,232,79,0.28)" strokeWidth="1.5" />
      <line x1="360" y1="81"  x2="378" y2="81"  stroke="rgba(110,232,79,0.28)" strokeWidth="1.5" />
      <line x1="334" y1="118" x2="360" y2="118" stroke="rgba(110,232,79,0.28)" strokeWidth="1.5" />
      <line x1="334" y1="144" x2="360" y2="144" stroke="rgba(110,232,79,0.28)" strokeWidth="1.5" />
      <line x1="360" y1="118" x2="360" y2="144" stroke="rgba(110,232,79,0.28)" strokeWidth="1.5" />
      <line x1="360" y1="131" x2="378" y2="131" stroke="rgba(110,232,79,0.28)" strokeWidth="1.5" />
      <line x1="378" y1="81"  x2="378" y2="131" stroke="rgba(110,232,79,0.20)" strokeWidth="1.5" />
      <line x1="378" y1="106" x2="393" y2="106" stroke="rgba(110,232,79,0.20)" strokeWidth="1.5" />
    </svg>
  );
}

function AnalystVisual() {
  return (
    <svg viewBox="0 0 400 200" fill="none" aria-hidden="true">
      <defs>
        <radialGradient id="analystGlow" cx="50%" cy="48%" r="60%">
          <stop offset="0%" stopColor="rgba(34,211,238,0.18)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>
      <rect width="400" height="200" fill="url(#analystGlow)" />
      <polygon points="154,28 218,62 218,132 154,168 90,132 90,62" stroke="rgba(34,211,238,0.18)" strokeWidth="1" />
      <polygon points="154,48 200,72 200,122 154,148 108,122 108,72" stroke="rgba(34,211,238,0.12)" strokeWidth="1" />
      <polygon points="154,61 190,80 190,114 154,135 118,114 118,80" fill="rgba(34,211,238,0.11)" stroke="#22d3ee" strokeOpacity="0.65" strokeWidth="1.5" />
      <path d="M154 28v33M218 62l-28 18M218 132l-28-18M154 168v-33M90 132l28-18M90 62l28 18" stroke="rgba(110,232,79,0.2)" strokeWidth="1" />
      <circle cx="154" cy="61" r="3" fill="#facc15" />
      <circle cx="190" cy="80" r="3" fill="#22d3ee" />
      <circle cx="190" cy="114" r="3" fill="#6ee84f" />
      <circle cx="154" cy="135" r="3" fill="#fb923c" />
      <circle cx="118" cy="114" r="3" fill="#ef4444" />
      <circle cx="118" cy="80" r="3" fill="#a855f7" />
      <rect x="264" y="47" width="86" height="12" rx="6" fill="rgba(34,211,238,0.14)" />
      <rect x="264" y="47" width="67" height="12" rx="6" fill="#22d3ee" fillOpacity="0.68" />
      <rect x="264" y="76" width="86" height="12" rx="6" fill="rgba(110,232,79,0.12)" />
      <rect x="264" y="76" width="53" height="12" rx="6" fill="#6ee84f" fillOpacity="0.68" />
      <rect x="264" y="105" width="86" height="12" rx="6" fill="rgba(250,204,21,0.12)" />
      <rect x="264" y="105" width="74" height="12" rx="6" fill="#facc15" fillOpacity="0.68" />
      <rect x="264" y="134" width="86" height="12" rx="6" fill="rgba(251,146,60,0.12)" />
      <rect x="264" y="134" width="44" height="12" rx="6" fill="#fb923c" fillOpacity="0.68" />
    </svg>
  );
}

const HOMEPAGE_FLAGSHIPS = {
  'thirty-eight-zero': {
    id: 'thirty-eight-zero',
    title: '38-0',
    badge: 'Daily 38-0 · New run every day',
    badgeIcon: 'target',
    heading: 'Can you build a team that goes 38-0?',
    copy: 'Draft your XI, simulate all 38 matches and chase the perfect season. Same challenge for everyone today.',
    primaryCta: 'Play today’s 38-0',
    primaryTo: '/38-0/daily',
    secondaryCta: 'Choose a mode',
    secondaryTo: '/38-0',
    featureKicker: 'Flagship experience',
    featureTitle: '38-0: The Perfect Season Challenge',
    featureCopy: 'Draft an all-time XI from real club-season squads, simulate 38 matches and chase the perfect unbeaten season.',
    featureCta: 'Build your unbeaten XI',
    featureTo: '/38-0',
    FeatureVisual: DraftVisual,
    accent: '#6ee84f',
    accentText: '#030f03',
    accentSoft: 'rgba(110,232,79,0.10)',
    accentBorder: 'rgba(110,232,79,0.34)',
    accentGlow: 'rgba(110,232,79,0.32)',
    shareText: 'Build an XI and chase the perfect unbeaten season. Play 38-0 free:',
    analyticsEvent: 'thirty_eight_zero_flagship_cta_clicked',
    seoTitle: '38-0 Football Draft Game | Build an Unbeaten XI | Squadverse',
    seoDescription: 'Build an all-time XI from real club-season squads, simulate 38 matches and chase the perfect unbeaten season. Play 38-0 free on Squadverse.',
  },
  'champions-xi': {
    id: 'champions-xi',
    title: 'Champions XI',
    badge: 'Champions League nights · Draft an all-time XI',
    badgeIcon: 'cup',
    heading: 'Can you build a Champions XI for Europe?',
    copy: 'Choose Classic, Club, or Daily from Champions League history, then chase a perfect 15-0 European campaign.',
    primaryCta: 'Play Champions XI',
    primaryTo: '/champions-xi',
    secondaryCta: 'Play Club mode',
    secondaryTo: '/champions-xi/play?mode=club',
    featureKicker: 'European nights',
    featureTitle: 'Champions XI: The European Draft',
    featureCopy: 'Choose Classic for the full draft, Club to build around one team, or Daily to take on the shared 15-0 challenge.',
    featureCta: 'Draft your XI',
    featureTo: '/champions-xi',
    FeatureVisual: ChampionsXiVisual,
    accent: '#f0b429',
    accentText: '#1d1604',
    accentSoft: 'rgba(240,180,41,0.12)',
    accentBorder: 'rgba(240,180,41,0.42)',
    accentGlow: 'rgba(240,180,41,0.30)',
    shareText: 'Draft a Champions League XI and chase a perfect 15-0 campaign on Squadverse:',
    analyticsEvent: 'champions_xi_flagship_cta_clicked',
    seoTitle: 'Champions XI Football Draft Game | Squadverse',
    seoDescription: 'Draft an all-time Champions League XI and chase a perfect 15-0 European campaign on Squadverse.',
  },
  'transfer-window-challenge': {
    id: 'transfer-window-challenge',
    title: 'Transfer Window Challenge',
    badge: 'Transfer window · Make every move count',
    badgeIcon: 'transfer',
    heading: 'Can you fix the transfer window?',
    copy: 'Pick a club, sell smart, buy sharper, then simulate the season under pressure.',
    primaryCta: 'Fix the window',
    primaryTo: '/transfer-window-challenge',
    secondaryCta: 'Build your own plan',
    secondaryTo: '/transfer-builder-setup',
    featureKicker: 'Transfer pressure',
    featureTitle: 'Transfer Window Challenge',
    featureCopy: 'Pick a club, sell smart, buy sharper, then run the season and see whether your window worked.',
    featureCta: 'Fix a transfer window',
    featureTo: '/transfer-window-challenge',
    FeatureVisual: TransferVisual,
    accent: '#38bdf8',
    accentText: '#03131d',
    accentSoft: 'rgba(56,189,248,0.12)',
    accentBorder: 'rgba(56,189,248,0.40)',
    accentGlow: 'rgba(56,189,248,0.28)',
    shareText: 'Fix a club’s transfer window, run the season, and see if your plan works on Squadverse:',
    analyticsEvent: 'transfer_window_flagship_cta_clicked',
    seoTitle: 'Transfer Window Challenge | Squadverse',
    seoDescription: 'Fix a club’s transfer window, make the right transfers, and simulate the season on Squadverse.',
  },
  'world-xi-cup': {
    id: 'world-xi-cup',
    title: 'World XI Cup',
    badge: 'International break · Build a World XI',
    badgeIcon: 'globe',
    heading: 'Who makes your World XI?',
    copy: 'Draft a squad from World Cup history and take it through a knockout tournament.',
    primaryCta: 'Start a World XI',
    primaryTo: '/world-xi-cup',
    secondaryCta: 'Play today’s XI',
    secondaryTo: '/world-xi-cup/daily',
    featureKicker: 'International football',
    featureTitle: 'World XI Cup',
    featureCopy: 'Draft a squad from World Cup history and take it through a knockout tournament.',
    featureCta: 'Start a draft',
    featureTo: '/world-xi-cup',
    FeatureVisual: WorldXIFeaturedVisual,
    accent: '#6ee84f',
    accentText: '#030f03',
    accentSoft: 'rgba(110,232,79,0.10)',
    accentBorder: 'rgba(110,232,79,0.34)',
    accentGlow: 'rgba(110,232,79,0.32)',
    shareText: 'Build a World XI and take it through a knockout tournament on Squadverse:',
    analyticsEvent: 'world_xi_flagship_cta_clicked',
    seoTitle: 'World XI Cup Football Draft Game | Squadverse',
    seoDescription: 'Build a World XI from World Cup history and take your squad through a knockout tournament on Squadverse.',
  },
};

export { HOMEPAGE_FLAGSHIPS };

// ─── Data ─────────────────────────────────────────────────────────────────────

const TOOLS = [
  {
    id: 'transfer',
    group: 'build',
    title: 'Transfer Builder',
    copy: 'Plan the dream window. Pick a club, set a budget, and build the squad your team needs.',
    cta: 'Build transfers',
    to: '/transfer-builder-setup',
    Visual: TransferVisual,
    accentBg: 'rgba(110,232,79,0.03)',
  },
  {
    id: 'transfer-window-challenge',
    group: 'play',
    title: 'Transfer Window Challenge',
    copy: 'Pick a club, sell smart, buy sharper, then simulate the season.',
    cta: 'Fix a transfer window',
    to: '/transfer-window-challenge',
    Visual: TransferVisual,
    accentBg: 'rgba(34,211,238,0.04)',
  },
  {
    id: 'lineup',
    group: 'build',
    title: 'Lineup Builder',
    copy: 'Pick your XI, choose a formation, and create a clean shareable team sheet.',
    cta: 'Create your XI',
    to: '/lineup-builder',
    Visual: LineupVisual,
    accentBg: 'rgba(56,189,248,0.03)',
  },
  {
    id: 'budgetsquad',
    group: 'build',
    title: 'Budget Squad Builder',
    copy: 'Build the best XI under budget — now with a Daily Challenge.',
    cta: 'Build squad',
    to: '/budget-squad',
    Visual: BudgetSquadVisual,
    accentBg: 'rgba(250,204,21,0.03)',
  },
  {
    id: 'tactics',
    group: 'build',
    title: 'Tactics Board',
    copy: 'Move players, draw runs, test shapes, and explain your football ideas visually.',
    cta: 'Plan tactics',
    to: '/tactics-board',
    Visual: TacticsVisual,
    accentBg: 'rgba(250,204,21,0.03)',
  },
  {
    id: 'analyst',
    group: 'analyse',
    title: 'Squadverse Analyst',
    copy: 'Search player profiles, compare styles, and find transparent statistical alternatives.',
    cta: 'Open Analyst',
    to: '/analyst',
    Visual: AnalystVisual,
    accentBg: 'rgba(34,211,238,0.04)',
  },
  {
    id: 'buykeepsell',
    group: 'debate',
    title: 'Buy / Keep / Sell',
    copy: 'Rebuild a squad. Decide who to buy, keep, and sell, then share your football take.',
    cta: 'Start Planning',
    to: '/buy-keep-sell',
    Visual: BuyKeepSellVisual,
    accentBg: 'rgba(168,85,247,0.03)',
  },
  {
    id: 'startbenchsell',
    group: 'debate',
    title: 'Start / Bench / Sell',
    copy: 'Pick who starts, who sits, and who gets sold in quick football debates.',
    cta: 'Play now',
    to: '/start-bench-sell',
    Visual: StartBenchSellVisual,
    accentBg: 'rgba(34,197,122,0.03)',
  },
  {
    id: 'tier-list',
    group: 'debate',
    title: 'Tier List Maker',
    copy: 'Rank players, managers, clubs, kits, and football debates in a Squadverse tier list.',
    cta: 'Create Tier List',
    to: '/tier-list',
    Visual: TierListVisual,
    accentBg: 'rgba(220,38,38,0.04)',
  },
  {
    id: 'draft',
    group: 'play',
    title: '38-0 Draft',
    copy: 'Build the squad and chase the perfect unbeaten season.',
    cta: 'Play 38-0',
    to: '/38-0',
    Visual: DraftVisual,
    accentBg: 'rgba(46,196,182,0.03)',
  },
  {
    id: 'champions-xi',
    group: 'play',
    title: 'Champions XI',
    copy: 'Play Classic, Club, or Daily drafts from Champions League history and chase a perfect 15-0 European campaign.',
    cta: 'Take the challenge',
    to: '/champions-xi',
    Visual: ChampionsXiVisual,
    accentBg: 'rgba(240,180,41,0.04)',
  },
  {
    id: 'cardclash',
    group: 'play',
    title: 'Squadverse Card Clash',
    copy: 'Battle football cards across eras — now with a Daily Cup Run.',
    cta: 'Play Card Clash',
    to: '/card-clash',
    Visual: CardClashVisual,
    accentBg: 'rgba(56,189,248,0.03)',
  },
  {
    id: 'auction-draft',
    group: 'play',
    title: 'Auction Draft',
    copy: 'Play solo against a CPU or pass the screen around as you bid, protect your budget, and build a five-a-side squad.',
    cta: 'Start bidding',
    to: '/auction-draft',
    Visual: AuctionDraftVisual,
    accentBg: 'rgba(250,204,21,0.04)',
  },
  {
    id: 'imposter',
    group: 'play',
    title: 'Squadverse Imposter',
    copy: 'Create a room and find the imposter in a live football hidden-role game.',
    cta: 'Play Imposter',
    to: '/imposter',
    Visual: ImposterVisual,
    accentBg: 'rgba(246,195,67,0.03)',
  },
  {
    id: 'alphabet',
    group: 'play',
    title: 'Squadverse Alphabet',
    copy: 'Create a room and race from A to Z naming players, clubs, countries and positions.',
    cta: 'Play Alphabet',
    to: '/alphabet',
    Visual: AlphabetVisual,
    accentBg: 'rgba(110,232,79,0.03)',
  },
  {
    id: 'squadbuilder',
    group: 'play',
    title: 'Squad Builder Challenge',
    copy: 'Complete football squad challenges with rules, ratings, and bragging rights — now with a Daily Challenge.',
    cta: 'Start Challenge',
    to: '/squad-builder',
    Visual: SquadBuilderVisual,
    accentBg: 'rgba(250,204,21,0.03)',
  },
];

const HOMEPAGE_TOOL_GROUPS = [
  {
    key: 'analyse',
    heading: 'Analyse',
    sub: 'Understand how players play with transparent public-data profiles.',
    tools: [
      {
        id: 'analyst',
        title: 'Squadverse Analyst',
        copy: 'Search players, compare styles, and find statistical alternatives.',
        cta: 'Open Analyst',
        to: '/analyst',
        Visual: AnalystVisual,
        accentBg: 'rgba(34,211,238,0.04)',
      },
    ],
  },
  {
    key: 'play',
    heading: 'Play',
    sub: 'Draft teams, chase unbeaten seasons, and play football party games.',
    tools: [
      {
        id: 'draft',
        title: '38-0',
        copy: 'Build the XI and chase a perfect unbeaten season.',
        cta: 'Play 38-0',
        to: '/38-0',
        Visual: DraftVisual,
        accentBg: 'rgba(46,196,182,0.03)',
      },
      {
        id: 'gauntlet',
        title: '38-0: The Gauntlet',
        copy: 'Draft one XI, face ten historic opponents, and survive the climb.',
        cta: 'Play The Gauntlet',
        to: '/38-0/gauntlet',
        Visual: () => <DraftVisual heading="THE GAUNTLET" record="10-0" caption="TEN-STAGE CLIMB" />,
        accentBg: 'rgba(110,232,79,0.04)',
      },
      {
        id: 'champions-xi',
        title: 'Champions XI',
        copy: 'Play Classic, Club, or Daily drafts from European history and chase a perfect 15-0 campaign.',
        cta: 'Take the challenge',
        to: '/champions-xi',
        Visual: ChampionsXiVisual,
        accentBg: 'rgba(240,180,41,0.04)',
      },
      {
        id: 'cardclash',
        title: 'Card Clash',
        copy: 'Battle football cards across eras and cup runs.',
        cta: 'Play Card Clash',
        to: '/card-clash',
        Visual: CardClashVisual,
        accentBg: 'rgba(56,189,248,0.03)',
      },
      {
        id: 'auction-draft',
        title: 'Auction Draft',
        copy: 'Play solo against a CPU or pass the screen around as you bid, protect your budget, and build a five-a-side squad.',
        cta: 'Start bidding',
        to: '/auction-draft',
        Visual: AuctionDraftVisual,
        accentBg: 'rgba(250,204,21,0.04)',
      },
      {
        id: 'squadbuilder',
        title: 'Squad Builder Challenge',
        copy: 'Build the best squad under special rules.',
        cta: 'Start Challenge',
        to: '/squad-builder',
        Visual: SquadBuilderVisual,
        accentBg: 'rgba(250,204,21,0.03)',
      },
      {
        id: 'imposter',
        title: 'Squadverse Imposter',
        copy: 'Find the hidden imposter in a football party game.',
        cta: 'Play Imposter',
        to: '/imposter',
        Visual: ImposterVisual,
        accentBg: 'rgba(246,195,67,0.03)',
      },
      {
        id: 'alphabet',
        title: 'Squadverse Alphabet',
        copy: 'Race from A to Z with football answers.',
        cta: 'Play Alphabet',
        to: '/alphabet',
        Visual: AlphabetVisual,
        accentBg: 'rgba(110,232,79,0.03)',
      },
    ],
  },
  {
    key: 'build',
    heading: 'Build',
    sub: 'Transfers, lineups, budgets, and tactical ideas.',
    tools: [
      {
        id: 'transfer',
        title: 'Transfer Builder',
        copy: 'Pick a club, set a budget, and plan the window.',
        cta: 'Build transfers',
        to: '/transfer-builder-setup',
        Visual: TransferVisual,
        accentBg: 'rgba(110,232,79,0.03)',
      },
      {
        id: 'lineup',
        title: 'Lineup Builder',
        copy: 'Pick your XI and create a clean team sheet.',
        cta: 'Create your XI',
        to: '/lineup-builder',
        Visual: LineupVisual,
        accentBg: 'rgba(56,189,248,0.03)',
      },
      {
        id: 'budgetsquad',
        title: 'Budget Squad Builder',
        copy: 'Build the strongest XI without overspending.',
        cta: 'Build squad',
        to: '/budget-squad',
        Visual: BudgetSquadVisual,
        accentBg: 'rgba(250,204,21,0.03)',
      },
      {
        id: 'tactics',
        title: 'Tactics Board',
        copy: 'Move players, draw runs, and test shapes.',
        cta: 'Plan tactics',
        to: '/tactics-board',
        Visual: TacticsVisual,
        accentBg: 'rgba(250,204,21,0.03)',
      },
    ],
  },
  {
    key: 'rank',
    heading: 'Rank',
    sub: 'Put players, clubs, kits, and managers into tiers.',
    tools: [
      {
        id: 'tier-list-maker',
        title: 'Tier List Maker',
        copy: 'Rank players, clubs, kits, and debates.',
        cta: 'Create Tier List',
        to: '/tier-list',
        Visual: TierListVisual,
        accentBg: 'rgba(220,38,38,0.04)',
      },
    ],
  },
  {
    key: 'debate',
    heading: 'Debate',
    sub: 'Make the call, share your picks, and settle the group chat.',
    tools: [
      {
        id: 'buykeepsell',
        title: 'Buy / Keep / Sell',
        copy: 'Decide who to buy, keep, and sell.',
        cta: 'Make your picks',
        to: '/buy-keep-sell',
        Visual: BuyKeepSellVisual,
        accentBg: 'rgba(168,85,247,0.03)',
      },
      {
        id: 'startbenchsell',
        title: 'Start / Bench / Sell',
        copy: 'Pick who starts, sits, and gets sold.',
        cta: 'Play now',
        to: '/start-bench-sell',
        Visual: StartBenchSellVisual,
        accentBg: 'rgba(34,197,122,0.03)',
      },
    ],
  },
];

const TOOLS_BY_ID = new Map(TOOLS.map((tool) => [tool.id, tool]));
const HEADER_TOOL_IDS = ['transfer', 'lineup', 'tactics', 'analyst'];
const MOBILE_TOOL_IDS = ['transfer', 'lineup', 'tactics', 'analyst'];

function getToolById(id) {
  return TOOLS_BY_ID.get(id) || null;
}

function getToolIcon(id) {
  return {
    transfer: 'transfer',
    'transfer-window-challenge': 'transfer',
    lineup: 'lineup',
    tactics: 'tactics',
    analyst: 'rank',
    budgetsquad: 'budgetsquad',
    buykeepsell: 'cycle',
    startbenchsell: 'bench',
    'tier-list': 'rank',
    draft: 'target',
    cardclash: 'card',
    'auction-draft': 'target',
    imposter: 'question',
    alphabet: 'alphabet',
    squadbuilder: 'squadbuilder',
  }[id] || 'question';
}

function NavToolLink({ tool, className, role, onClick, showBadge = false }) {
  if (!tool) return null;
  return (
    <Link to={tool.to} className={className} role={role} onClick={onClick}>
      <NavIcon id={getToolIcon(tool.id)} className="hp-dropdown-icon" />
      <span>{tool.title}</span>
      {showBadge && tool.badge && <span className="hp-nav-badge">{tool.badge}</span>}
    </Link>
  );
}

const CONTACT_EMAIL = 'squadverse2025@gmail.com';

function buildContactMailto({ name, email, topic, message }) {
  const subject = topic || 'Squadverse message';
  const body = [
    name ? `Name: ${name}` : null,
    email ? `Reply email: ${email}` : null,
    '',
    message || ''
  ].filter((line) => line !== null).join('\n');

  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

const PARTNERSHIP_TOPIC = 'Partnership enquiry — Squadverse';
const PARTNERSHIP_MAILTO = buildContactMailto({
  topic: PARTNERSHIP_TOPIC,
  message: [
    'Hi Squadverse,',
    '',
    "I'm [name] from [brand / organisation].",
    'Website or social link:',
    '',
    'We’re interested in: [advertising / sponsoring a challenge / collaboration]',
    'Our idea:',
    'Timing and budget, if known:',
    '',
    'Thanks!',
  ].join('\n'),
});

// 4-3-3 positions tuned to sit cleanly on the homepage tilted pitch preview.
const TILTED_SHIRT_SIZE = {
  width: 'clamp(36px, 4vw, 52px)',
  height: 'clamp(32px, 3.6vw, 46px)',
};

const TILTED_SLOTS = [
  { top: '80%', left: '50%', gk: true, name: 'Alisson', number: 1, nation: 'Brazil' }, // GK
  { top: '63%', left: '16%', gk: false, name: 'R. Carlos', number: 6, nation: 'Brazil' }, // LB
  { top: '64%', left: '34%', gk: false, name: 'van Dijk', number: 4, nation: 'Netherlands' }, // LCB
  { top: '64%', left: '66%', gk: false, name: 'Maldini', number: 3, nation: 'Italy' }, // RCB
  { top: '63%', left: '84%', gk: false, name: 'Cafu', number: 2, nation: 'Brazil' }, // RB
  { top: '43%', left: '30%', gk: false, name: 'Gullit', number: 10, nation: 'Netherlands' }, // LM
  { top: '41%', left: '50%', gk: false, name: 'Gerrard', number: 8, nation: 'England' }, // CM
  { top: '43%', left: '70%', gk: false, name: 'Zidane', number: 5, nation: 'France' }, // RM
  { top: '22%', left: '27%', gk: false, name: 'C. Ronaldo', number: 7, nation: 'Portugal' }, // LW
  { top: '17%', left: '50%', gk: false, name: 'Ronaldo', number: 9, nation: 'Brazil' }, // CF
  { top: '22%', left: '73%', gk: false, name: 'Messi', number: 10, nation: 'Argentina' }, // RW
];

// Kit colors for the homepage hero XI — tuned for accuracy at a glance and
// legibility against the dark navy hero background (not the same map as
// NATION_KIT_COLORS, which is tuned for goal-flash banner contrast instead).
const HERO_KIT_COLORS = {
  Brazil: { color: '#FFCC00', textColor: '#0b2b13' },
  Netherlands: { color: '#FF6C00', textColor: '#1f2937' },
  Italy: { color: '#1E5AC8', textColor: '#ffffff' },
  England: { color: '#F5F5F5', textColor: '#1f2937' },
  France: { color: '#0F5FD0', textColor: '#ffffff' },
  Portugal: { color: '#D91E36', textColor: '#ffffff' },
  Argentina: { color: '#75AADB', pattern: 'stripes', stripeColor: '#ffffff', textColor: '#12305c' },
};

const HERO_GK_KIT = { color: '#16a34a', textColor: '#ffffff' };

function getHeroKit(slot) {
  if (slot.gk) return HERO_GK_KIT;
  return HERO_KIT_COLORS[slot.nation] || HERO_KIT_COLORS.Brazil;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ToolCard({ tool }) {
  const { title, copy, cta, to, Visual, accentBg, badge } = tool;
  return (
    <Link
      to={to}
      className="hp-tool-card"
      aria-label={`${title} — ${cta}`}
    >
      <div className="hp-card-visual" style={{ background: accentBg }}>
        {badge && <span className="hp-card-badge">{badge}</span>}
        <Visual />
      </div>
      <div className="hp-card-body">
        <h4 className="hp-card-title">{title}</h4>
        <p className="hp-card-copy">{copy}</p>
        <span className="hp-card-cta" aria-hidden="true">
          {cta} <span>→</span>
        </span>
      </div>
    </Link>
  );
}

const WORLD_CUP_MODES = [
  {
    id: 'classic',
    title: 'World XI Cup: Classic',
    badge: 'RECOMMENDED',
    accent: '#6ee84f',
    cardBg: 'linear-gradient(135deg, rgba(110,232,79,0.14) 0%, rgba(255,255,255,0.03) 100%)',
    borderColor: 'rgba(110,232,79,0.45)',
    badgeBg: 'rgba(110,232,79,0.16)',
    copy: 'Spin for a historic World Cup squad and pick one player per spin to build your XI.',
    cta: 'Play Classic',
    to: '/world-xi-cup/classic',
  },
  {
    id: 'daily',
    title: 'World XI Cup: Daily Challenge',
    badge: 'NEW',
    accent: '#5b8cff',
    cardBg: 'linear-gradient(135deg, rgba(91,140,255,0.16) 0%, rgba(255,255,255,0.03) 100%)',
    borderColor: 'rgba(91,140,255,0.45)',
    badgeBg: 'rgba(91,140,255,0.18)',
    copy: 'Same daily-seeded spins for everyone. One official attempt a day.',
    cta: 'Play Daily Challenge',
    to: '/world-xi-cup/daily',
  },
  {
    id: 'pack-draft',
    title: 'World XI Cup: Pack Draft',
    badge: 'FAST MODE',
    accent: '#fb923c',
    cardBg: 'linear-gradient(135deg, rgba(251,146,60,0.14) 0%, rgba(255,255,255,0.03) 100%)',
    borderColor: 'rgba(251,146,60,0.45)',
    badgeBg: 'rgba(251,146,60,0.16)',
    copy: 'Open World Cup player packs by position for a quicker squad build.',
    cta: 'Play Pack Draft',
    to: '/world-xi-cup/pack-draft',
  },
];

function WorldCupModeCard({ mode }) {
  return (
    <Link
      to={mode.to}
      className="hp-wc-card"
      style={{ background: mode.cardBg, borderColor: mode.borderColor }}
      aria-label={`${mode.title} — ${mode.cta}`}
    >
      <span
        className="hp-wc-card-badge"
        style={{ background: mode.badgeBg, color: mode.accent, borderColor: mode.borderColor }}
      >
        {mode.badge}
      </span>
      <h3 className="hp-wc-card-title">{mode.title}</h3>
      <p className="hp-wc-card-copy">{mode.copy}</p>
      <span className="hp-wc-card-cta" style={{ color: mode.accent }}>
        {mode.cta} <span aria-hidden="true">→</span>
      </span>
    </Link>
  );
}

function WorldCupSeasonSection() {
  return (
    <>
      <div className="hp-wc-season-eyebrow">
        <NavIcon id="globe" /> World Cup season
      </div>
      <h2 className="hp-wc-season-title" id="hp-wc-season-title">World Cup Season on Squadverse</h2>
      <p className="hp-wc-season-sub">Build your World XI, predict the knockout route, and chase the trophy before kickoff.</p>
      <div className="hp-wc-season-grid">
        {WORLD_CUP_MODES.map((mode) => (
          <WorldCupModeCard key={mode.id} mode={mode} />
        ))}
      </div>
      <Link to="/world-xi-cup" className="hp-wc-season-hub-link">
        See the full World XI Cup hub &amp; FAQ <span aria-hidden="true">→</span>
      </Link>
    </>
  );
}

export const DAILY_CHALLENGES = [
  {
    id: 'gauntlet-daily',
    title: 'Daily Gauntlet',
    badge: 'Survival',
    accent: '#facc15',
    cardBg: 'linear-gradient(135deg, rgba(250,204,21,0.16) 0%, rgba(255,255,255,0.03) 100%)',
    borderColor: 'rgba(250,204,21,0.4)',
    badgeBg: 'rgba(250,204,21,0.16)',
    copy: 'Same draft and ten opponents for everyone. One official attempt. A fresh climb at midnight UK time.',
    cta: 'Play today’s Gauntlet',
    to: '/38-0/gauntlet/daily',
  },
  {
    id: 'thirty-eight-zero-daily',
    title: 'Daily 38-0',
    badge: 'Streak',
    accent: '#fb923c',
    cardBg: 'linear-gradient(135deg, rgba(251,146,60,0.16) 0%, rgba(255,255,255,0.03) 100%)',
    borderColor: 'rgba(251,146,60,0.4)',
    badgeBg: 'rgba(251,146,60,0.16)',
    copy: 'Take on today’s fixed 38-match run. Build the XI, survive the season, and chase 38-0.',
    cta: 'Play today’s run',
    to: '/38-0/daily',
  },
  {
    id: 'world-xi-daily',
    title: 'World XI Daily',
    badge: 'World Cup',
    accent: '#6ee84f',
    cardBg: 'linear-gradient(135deg, rgba(110,232,79,0.16) 0%, rgba(255,255,255,0.03) 100%)',
    borderColor: 'rgba(110,232,79,0.4)',
    badgeBg: 'rgba(110,232,79,0.16)',
    copy: 'Spin the same World Cup squads as everyone today. Build your XI and chase the trophy.',
    cta: 'Play Daily XI',
    to: '/world-xi-cup/daily',
  },
  {
    id: 'budget-squad-daily',
    title: 'Daily Budget Squad',
    badge: 'Budget',
    accent: '#facc15',
    cardBg: 'linear-gradient(135deg, rgba(250,204,21,0.16) 0%, rgba(255,255,255,0.03) 100%)',
    borderColor: 'rgba(250,204,21,0.4)',
    badgeBg: 'rgba(250,204,21,0.16)',
    copy: 'Same budget, same rules. Build the best XI you can without overspending.',
    cta: 'Build Today’s Squad',
    to: '/budget-squad/daily',
  },
  {
    id: 'card-clash-daily',
    title: 'Daily Cup Run',
    badge: 'Cards',
    accent: '#38bdf8',
    cardBg: 'linear-gradient(135deg, rgba(56,189,248,0.16) 0%, rgba(255,255,255,0.03) 100%)',
    borderColor: 'rgba(56,189,248,0.4)',
    badgeBg: 'rgba(56,189,248,0.16)',
    copy: 'Same cards, same bracket. Battle through Card Clash and lift the cup.',
    cta: 'Start Cup Run',
    to: '/card-clash/daily',
  },
  {
    id: 'squad-builder-daily',
    title: 'Daily Squad Builder',
    badge: 'Puzzle',
    accent: '#b7ff8a',
    cardBg: 'linear-gradient(135deg, rgba(183,255,138,0.16) 0%, rgba(255,255,255,0.03) 100%)',
    borderColor: 'rgba(183,255,138,0.4)',
    badgeBg: 'rgba(183,255,138,0.16)',
    copy: 'Solve today’s squad puzzle with locked rules and a shared draft.',
    cta: 'Start Challenge',
    to: '/squad-builder/daily',
  },
  {
    id: 'champions-xi-daily',
    title: 'Champions XI Daily',
    badge: 'European',
    accent: '#f0b429',
    cardBg: 'linear-gradient(135deg, rgba(240,180,41,0.16) 0%, rgba(255,255,255,0.03) 100%)',
    borderColor: 'rgba(240,180,41,0.4)',
    badgeBg: 'rgba(240,180,41,0.16)',
    copy: 'Same formation, spins and opponents as everyone today. Chase the 15-0 run.',
    cta: 'Play today’s draft',
    to: '/champions-xi/daily',
  },
];

function DailyChallengeCard({ card }) {
  return (
    <Link
      to={card.to}
      className="hp-daily-card"
      style={{ background: card.cardBg, borderColor: card.borderColor }}
      aria-label={`${card.title} — ${card.cta}`}
    >
      <span
        className="hp-daily-card-badge"
        style={{ background: card.badgeBg, color: card.accent, borderColor: card.borderColor }}
      >
        {card.badge}
      </span>
      <h3 className="hp-daily-card-title">{card.title}</h3>
      <p className="hp-daily-card-copy">{card.copy}</p>
      <span className="hp-daily-card-cta" style={{ color: card.accent }}>
        {card.cta} <span aria-hidden="true">→</span>
      </span>
    </Link>
  );
}

function DailyChallengesSection() {
  return (
    <>
      <div className="hp-daily-eyebrow">
        <NavIcon id="bolt" /> Daily Challenges
      </div>
      <h2 className="hp-daily-title" id="hp-daily-section-title">Today on Squadverse</h2>
      <p className="hp-daily-sub">{DAILY_CHALLENGES.length} football challenges. Same setup for everyone. Fresh every day.</p>
      <div className="hp-daily-grid">
        {DAILY_CHALLENGES.map((card) => (
          <DailyChallengeCard key={card.id} card={card} />
        ))}
      </div>
      <p className="hp-daily-footer-note">Come back tomorrow for a new set.</p>
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function buildSharePayload(flagship) {
  return {
    title: `${flagship.title} on Squadverse`,
    text: flagship.shareText,
    url: `https://squadverse.uk${flagship.primaryTo}`,
  };
}

function HomePage() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [shareStatus, setShareStatus] = useState('');
  const [homepageNow, setHomepageNow] = useState(() => new Date());
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    topic: 'Squadverse idea',
    message: '',
  });

  useEffect(() => {
    const refreshHomepageContext = () => setHomepageNow(new Date());
    const intervalId = window.setInterval(refreshHomepageContext, 15 * 60 * 1000);
    window.addEventListener('focus', refreshHomepageContext);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshHomepageContext);
    };
  }, []);

  const homepageSelection = selectHomepageFlagship({
    now: homepageNow,
    events: HOMEPAGE_CALENDAR_EVENTS,
  });
  const flagship = HOMEPAGE_FLAGSHIPS[homepageSelection.flagshipId]
    || HOMEPAGE_FLAGSHIPS[DEFAULT_HOMEPAGE_FLAGSHIP_ID];
  const FeatureVisual = flagship.FeatureVisual;
  const sharePayload = buildSharePayload(flagship);

  const shareSquadverse = async () => {
    if (navigator.share) {
      try {
        await navigator.share(sharePayload);
      } catch (error) {
        if (error?.name !== 'AbortError') {
          setShareStatus('Could not open share sheet.');
          setTimeout(() => setShareStatus(''), 2000);
        }
      }
      return;
    }
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(sharePayload.url);
        setShareStatus('Link copied!');
      } catch (_) {
        setShareStatus('Could not copy link.');
      }
      setTimeout(() => setShareStatus(''), 2000);
    }
  };

  const openContactModal = (topic = 'Squadverse idea') => {
    setContactForm((form) => ({ ...form, topic }));
    setContactModalOpen(true);
  };

  const updateContactForm = (event) => {
    const { name, value } = event.target;
    setContactForm((form) => ({ ...form, [name]: value }));
  };

  const sendContactEmail = (event) => {
    event.preventDefault();
    window.location.href = buildContactMailto(contactForm);
    setContactModalOpen(false);
  };

  useEffect(() => {
    if (window.location.hash !== '#hp-daily-challenges') return;
    const dailySection = document.getElementById('hp-daily-challenges');
    if (dailySection) dailySection.scrollIntoView({ block: 'start' });
  }, []);

  return (
    <>
      <Helmet>
        <title>{flagship.seoTitle}</title>
        <meta
          name="description"
          content={flagship.seoDescription}
        />
        <meta property="og:title" content={flagship.seoTitle} />
        <meta property="og:description" content={flagship.seoDescription} />
        <meta property="og:image" content="https://www.squadverse.uk/og/home.png" />
        <meta property="og:image:alt" content={`${flagship.title} on Squadverse`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.squadverse.uk/" />
        <meta property="og:site_name" content="Squadverse" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={flagship.seoTitle} />
        <meta
          name="twitter:description"
          content={flagship.seoDescription}
        />
        <meta name="twitter:image" content="https://www.squadverse.uk/og/home.png" />
        <link rel="canonical" href="https://www.squadverse.uk/" />
      </Helmet>

      <div
        className={`hp-root hp-root--${flagship.id}`}
        style={{
          '--hp-flagship-accent': flagship.accent,
          '--hp-flagship-accent-text': flagship.accentText,
          '--hp-flagship-accent-soft': flagship.accentSoft,
          '--hp-flagship-accent-border': flagship.accentBorder,
          '--hp-flagship-accent-glow': flagship.accentGlow,
        }}
      >

        {/* ── HEADER ───────────────────────────────────────────────── */}
        <header className="hp-header">
          <Link to="/" className="hp-logo" aria-label="Squadverse home">
            <img
              src={process.env.PUBLIC_URL + '/squadverse.png'}
              alt="Squadverse"
              className="hp-logo-img"
            />
          </Link>

          <nav aria-label="Main navigation">
            <ul className="hp-nav" role="list">
              <li className="hp-nav-item">
                <button className="hp-nav-link" aria-haspopup="true">
                  Tools <span className="hp-nav-chevron" aria-hidden="true">▾</span>
                </button>
                <div className="hp-dropdown" role="menu">
                  {HEADER_TOOL_IDS.map((toolId) => (
                    <NavToolLink
                      key={toolId}
                      tool={getToolById(toolId)}
                      className="hp-dropdown-link"
                      role="menuitem"
                      showBadge
                    />
                  ))}
                </div>
              </li>
              <li className="hp-nav-item">
                <button className="hp-nav-link" aria-haspopup="true">
                  Debate <span className="hp-nav-chevron" aria-hidden="true">▾</span>
                </button>
                <div className="hp-dropdown" role="menu">
                  <Link to="/buy-keep-sell" className="hp-dropdown-link" role="menuitem">
                    <NavIcon id="cycle" className="hp-dropdown-icon" />
                    Buy / Keep / Sell
                  </Link>
                  <Link to="/start-bench-sell" className="hp-dropdown-link" role="menuitem">
                    <NavIcon id="bench" className="hp-dropdown-icon" />
                    Start / Bench / Sell
                  </Link>
                  <Link to="/tier-list" className="hp-dropdown-link" role="menuitem">
                    <NavIcon id="rank" className="hp-dropdown-icon" />
                    Tier List Maker
                  </Link>
                </div>
              </li>
              <li className="hp-nav-item">
                <button className="hp-nav-link" aria-haspopup="true">
                  Games <span className="hp-nav-chevron" aria-hidden="true">▾</span>
                </button>
                <div className="hp-dropdown" role="menu">
                  <NavToolLink
                    tool={getToolById('transfer-window-challenge')}
                    className="hp-dropdown-link"
                    role="menuitem"
                    showBadge
                  />
                  <a href="#hp-daily-challenges" className="hp-dropdown-link" role="menuitem">
                    <NavIcon id="bolt" className="hp-dropdown-icon" />
                    Today&rsquo;s Daily Challenges
                  </a>
                  <Link to="/world-xi-cup" className="hp-dropdown-link" role="menuitem">
                    <NavIcon id="globe" className="hp-dropdown-icon" />
                    World XI Cup
                  </Link>
                  <Link to="/38-0" className="hp-dropdown-link" role="menuitem">
                    <NavIcon id="target" className="hp-dropdown-icon" />
                    38-0 Draft
                  </Link>
                  <Link to="/champions-xi" className="hp-dropdown-link" role="menuitem">
                    <NavIcon id="cup" className="hp-dropdown-icon" />
                    Champions XI
                  </Link>
                  <Link to="/card-clash" className="hp-dropdown-link" role="menuitem">
                    <NavIcon id="card" className="hp-dropdown-icon" />
                    Squadverse Card Clash
                  </Link>
                  <Link to="/auction-draft" className="hp-dropdown-link" role="menuitem">
                    <NavIcon id="target" className="hp-dropdown-icon" />
                    Auction Draft
                  </Link>
                  <Link to="/imposter" className="hp-dropdown-link" role="menuitem">
                    <NavIcon id="question" className="hp-dropdown-icon" />
                    Squadverse Imposter
                  </Link>
                  <Link to="/alphabet" className="hp-dropdown-link" role="menuitem">
                    <NavIcon id="alphabet" className="hp-dropdown-icon" />
                    Squadverse Alphabet
                  </Link>
                  <Link to="/squad-builder" className="hp-dropdown-link" role="menuitem">
                    <NavIcon id="squadbuilder" className="hp-dropdown-icon" />
                    Squad Builder Challenge
                  </Link>
                  <Link to="/budget-squad" className="hp-dropdown-link" role="menuitem">
                    <NavIcon id="budgetsquad" className="hp-dropdown-icon" />
                    Budget Squad Builder
                  </Link>
                </div>
              </li>
              <li>
                <a href="mailto:squadverse2025@gmail.com" className="hp-nav-link">
                  Contact
                </a>
              </li>
            </ul>
          </nav>

          <div className="hp-header-actions">
            <button
              type="button"
              className="hp-btn-share"
              onClick={shareSquadverse}
              aria-label="Share Squadverse with a friend"
            >
              <NavIcon id="share" /> {shareStatus || 'Share with a mate'}
            </button>
            <a
              href="https://buymeacoffee.com/ammarabuyahya"
              target="_blank"
              rel="noopener noreferrer"
              className="hp-btn-coffee"
            >
              ☕ Buy me a coffee
            </a>
          </div>

          <button
            className="hp-hamburger"
            aria-label={mobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen(true)}
          >
            <span />
            <span />
            <span />
          </button>
        </header>

        {/* ── MOBILE NAV ───────────────────────────────────────────── */}
        <div
          className={`hp-mobile-nav${mobileNavOpen ? ' is-open' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          <button
            className="hp-mobile-nav-close"
            onClick={() => setMobileNavOpen(false)}
            aria-label="Close navigation"
          >
            ✕
          </button>

          <span className="hp-mobile-nav-section-title">Games</span>
          <a href="#hp-daily-challenges" onClick={() => setMobileNavOpen(false)}><NavIcon id="bolt" /> Today&rsquo;s Daily Challenges</a>
          <NavToolLink
            tool={getToolById('transfer-window-challenge')}
            className="hp-mobile-nav-link"
            onClick={() => setMobileNavOpen(false)}
            showBadge
          />
          <Link to="/world-xi-cup" onClick={() => setMobileNavOpen(false)}><NavIcon id="globe" className="hp-dropdown-icon" /> World XI Cup</Link>
          <Link to="/38-0" onClick={() => setMobileNavOpen(false)}><NavIcon id="target" className="hp-dropdown-icon" /> 38-0 Draft</Link>
          <Link to="/champions-xi" onClick={() => setMobileNavOpen(false)}><NavIcon id="cup" className="hp-dropdown-icon" /> Champions XI</Link>
          <Link to="/card-clash" onClick={() => setMobileNavOpen(false)}><NavIcon id="card" className="hp-dropdown-icon" /> Squadverse Card Clash</Link>
          <Link to="/auction-draft" onClick={() => setMobileNavOpen(false)}><NavIcon id="target" className="hp-dropdown-icon" /> Auction Draft</Link>
          <Link to="/imposter" onClick={() => setMobileNavOpen(false)}><NavIcon id="question" className="hp-dropdown-icon" /> Squadverse Imposter</Link>
          <Link to="/alphabet" onClick={() => setMobileNavOpen(false)}><NavIcon id="alphabet" className="hp-dropdown-icon" /> Squadverse Alphabet</Link>
          <Link to="/squad-builder" onClick={() => setMobileNavOpen(false)}><NavIcon id="squadbuilder" className="hp-dropdown-icon" /> Squad Builder Challenge</Link>
          <Link to="/budget-squad" onClick={() => setMobileNavOpen(false)}><NavIcon id="budgetsquad" className="hp-dropdown-icon" /> Budget Squad Builder</Link>

          <span className="hp-mobile-nav-section-title">Debate</span>
          <Link to="/buy-keep-sell" onClick={() => setMobileNavOpen(false)}><NavIcon id="cycle" className="hp-dropdown-icon" /> Buy / Keep / Sell</Link>
          <Link to="/start-bench-sell" onClick={() => setMobileNavOpen(false)}><NavIcon id="bench" className="hp-dropdown-icon" /> Start / Bench / Sell</Link>
          <Link to="/tier-list" onClick={() => setMobileNavOpen(false)}><NavIcon id="rank" className="hp-dropdown-icon" /> Tier List Maker</Link>

          <span className="hp-mobile-nav-section-title">Tools</span>
          {MOBILE_TOOL_IDS.map((toolId) => (
            <NavToolLink
              key={toolId}
              tool={getToolById(toolId)}
              className="hp-mobile-nav-link"
              onClick={() => setMobileNavOpen(false)}
              showBadge
            />
          ))}

          <span className="hp-mobile-nav-section-title">More</span>
          <a href="mailto:squadverse2025@gmail.com" onClick={() => setMobileNavOpen(false)}><NavIcon id="mail" /> Contact</a>

          <div className="hp-mobile-coffee">
            <button
              type="button"
              className="hp-btn-share"
              style={{ width: '100%', justifyContent: 'center', minHeight: '52px', fontSize: '1rem', marginBottom: '0.75rem' }}
              onClick={shareSquadverse}
              aria-label="Share Squadverse with a friend"
            >
              <NavIcon id="share" /> {shareStatus || 'Share with a mate'}
            </button>
            <a
              href="https://buymeacoffee.com/ammarabuyahya"
              target="_blank"
              rel="noopener noreferrer"
              className="hp-btn-coffee"
              style={{ width: '100%', justifyContent: 'center', minHeight: '52px', fontSize: '1rem' }}
              onClick={() => setMobileNavOpen(false)}
            >
              ☕ Buy me a coffee
            </a>
          </div>
        </div>

        <main id="main-content">

          {/* ── HERO ─────────────────────────────────────────────── */}
          <section className={`hp-hero hp-hero--${flagship.id}`} aria-labelledby="hp-hero-title">
            <div className="hp-hero-text">
              <div className="hp-hero-badge">
                <NavIcon id={flagship.badgeIcon} /> {flagship.badge}
              </div>

              <h1 className="hp-hero-h1" id="hp-hero-title">
                {flagship.heading}
              </h1>

              <p className="hp-hero-copy">
                {flagship.copy}
              </p>

              <div className="hp-hero-actions">
                <Link
                  to={flagship.primaryTo}
                  className="hp-btn-primary"
                  onClick={() => trackEvent(flagship.analyticsEvent, { placement: 'homepage_hero' })}
                >
                  {flagship.primaryCta} <span className="hp-btn-arrow" aria-hidden="true">→</span>
                </Link>
                <Link to={flagship.secondaryTo} className="hp-btn-ghost">
                  {flagship.secondaryCta} <span className="hp-btn-arrow" aria-hidden="true">→</span>
                </Link>
              </div>

              <p className="hp-hero-trust">
                Football games built for the group chat. No account needed.
              </p>
            </div>

            <div className="hp-hero-visual" aria-hidden="true">
              <div className="hp-pitch-wrap">
                <img
                  src={process.env.PUBLIC_URL + '/pitch_tilted.png'}
                  alt=""
                  className="hp-pitch-img"
                />
                <div className="hp-pitch-overlay">
                  {TILTED_SLOTS.map((slot, i) => {
                    const kit = getHeroKit(slot);
                    return (
                      <div
                        key={i}
                        className="hp-pitch-shirt"
                        style={{ left: slot.left, top: slot.top }}
                      >
                        <div className="hp-pitch-shirt-icon-wrap">
                          <ShirtIcon
                            color={kit.color}
                            pattern={kit.pattern}
                            stripeColor={kit.stripeColor}
                            width={TILTED_SHIRT_SIZE.width}
                            height={TILTED_SHIRT_SIZE.height}
                          />
                          <span className="hp-pitch-shirt-number" style={{ color: kit.textColor }}>
                            {slot.number}
                          </span>
                        </div>
                        <div className="hp-pitch-player-label">
                          <span className="hp-pitch-player-name">{slot.name}</span>
                        </div>
                      </div>
                    );
                  })}
                  <div className="hp-pitch-chrome-tl">
                    <img
                      src={process.env.PUBLIC_URL + '/squadverse.png'}
                      alt=""
                      className="hp-pitch-badge-img"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── FEATURED EXPERIENCES ─────────────────────────────── */}
          <section className="hp-featured" aria-labelledby="hp-featured-title">
            <div className="hp-section-intro">
              <span className="hp-section-eyebrow">Start here</span>
              <h2 className="hp-section-heading" id="hp-featured-title">Featured experiences</h2>
              <p className="hp-section-sub">Rebuild a club, take on today&rsquo;s challenges or draft your World XI.</p>
            </div>

            <div className="hp-featured-grid">
              {flagship.id === 'champions-xi' ? (
                <div
                  className={`hp-feature-card hp-feature-card--primary hp-feature-card--${flagship.id}`}
                  role="group"
                  aria-labelledby="hp-champions-xi-feature-title"
                >
                  <div className="hp-feature-card-content">
                    <span className="hp-feature-kicker">{flagship.featureKicker}</span>
                    <h3 id="hp-champions-xi-feature-title">{flagship.featureTitle}</h3>
                    <p>{flagship.featureCopy}</p>
                    <div className="hp-feature-links">
                      <Link to="/champions-xi" className="hp-feature-cta">
                        Open Champions XI hub <span aria-hidden="true">→</span>
                      </Link>
                      <Link to="/champions-xi/play?mode=club" className="hp-feature-cta hp-feature-cta--secondary">
                        Play Club mode <span aria-hidden="true">→</span>
                      </Link>
                    </div>
                  </div>
                  <div className="hp-feature-visual" aria-hidden="true"><FeatureVisual /></div>
                </div>
              ) : (
                <Link
                  to={flagship.featureTo}
                  className={`hp-feature-card hp-feature-card--primary hp-feature-card--${flagship.id}`}
                  aria-label={`${flagship.title} — ${flagship.featureCta}`}
                >
                  <div className="hp-feature-card-content">
                    <span className="hp-feature-kicker">{flagship.featureKicker}</span>
                    <h3>{flagship.featureTitle}</h3>
                    <p>{flagship.featureCopy}</p>
                    <span className="hp-feature-cta">{flagship.featureCta} <span aria-hidden="true">→</span></span>
                  </div>
                  <div className="hp-feature-visual" aria-hidden="true"><FeatureVisual /></div>
                </Link>
              )}

              <a
                href="#hp-daily-challenges"
                className="hp-feature-card hp-feature-card--daily"
                aria-label="Today’s Challenges — Play today"
              >
                <span className="hp-feature-icon" aria-hidden="true"><NavIcon id="bolt" /></span>
                <div className="hp-feature-card-content">
                  <span className="hp-feature-kicker">Fresh today</span>
                  <h3>Today&rsquo;s Challenges</h3>
                  <p>{DAILY_CHALLENGES.length} football challenges. Same setup for everyone. Fresh every day.</p>
                  <span className="hp-feature-cta">Play today <span aria-hidden="true">→</span></span>
                </div>
              </a>

              <Link
                to="/world-xi-cup"
                className="hp-feature-card hp-feature-card--world-xi"
                aria-label="World XI Cup — Start a draft"
              >
                <span className="hp-feature-icon" aria-hidden="true"><NavIcon id="globe" /></span>
                <div className="hp-feature-card-content">
                  <span className="hp-feature-kicker">Tournament draft</span>
                  <h3>World XI Cup</h3>
                  <p>Draft your squad and take it through a knockout tournament.</p>
                  <span className="hp-feature-cta">Start a draft <span aria-hidden="true">→</span></span>
                </div>
                <span className="hp-feature-watermark" aria-hidden="true"><WorldXIFeaturedVisual /></span>
              </Link>
            </div>

            <Link
              to="/38-0/play?mode=club"
              className="hp-club-promo"
              aria-label="Play the 38-0 Club Challenge"
            >
              <span className="hp-club-promo-icon" aria-hidden="true"><NavIcon id="target" /></span>
              <span className="hp-club-promo-content">
                <span className="hp-club-promo-kicker">New in 38-0</span>
                <span className="hp-club-promo-title">Choose a club. Build its historical XI.</span>
                <span className="hp-club-promo-copy">
                  Draft only from your chosen club&rsquo;s domestic league squads across seasons, then play all 38 matches.
                </span>
              </span>
              <span className="hp-club-promo-cta">Play Club Challenge <span aria-hidden="true">→</span></span>
            </Link>
          </section>

          <AdSense slot={ADSENSE_SLOTS.homeFeatured} />

          {/* ── DAILY CHALLENGES ─────────────────────────────────── */}
          <section id="hp-daily-challenges" className="hp-daily" aria-labelledby="hp-daily-section-title">
            <DailyChallengesSection />
          </section>

          <SurfsharkAffiliate variant="full" placement="homepage" />

          {/* ── TOOLS GRID ───────────────────────────────────────── */}
          <section id="hp-tools" className="hp-tools hp-explore" aria-labelledby="hp-tools-title">
            <span className="hp-section-eyebrow">Explore</span>
            <h2 className="hp-section-heading" id="hp-tools-title">
              Explore by category
            </h2>
            <p className="hp-section-sub">More ways to play, build squads, rank players and settle football debates.</p>

            {HOMEPAGE_TOOL_GROUPS.map((group) => (
              <div className={`hp-tool-group hp-tool-group--${group.key}`} key={group.key}>
                <span className="hp-group-eyebrow" aria-hidden="true">{group.heading}</span>
                <h3 className="hp-group-title">{group.heading}</h3>
                <p className="hp-group-sub">{group.sub}</p>
                <div className="hp-tools-grid">
                  {group.tools.map((tool) => (
                    <ToolCard key={tool.id} tool={tool} />
                  ))}
                </div>
              </div>
            ))}

            {/* Feedback / suggestion card */}
            <button
              type="button"
              className="hp-tool-card--suggest"
              onClick={() => openContactModal('Squadverse idea')}
              aria-label="Suggest an idea — send a message"
            >
              <NavIcon id="bulb" className="hp-suggest-icon" size={22} />
              <div className="hp-suggest-content">
                <span className="hp-suggest-title">Got a football game idea?</span>
                <p className="hp-suggest-copy">Got an idea for a game, mode or tool? Send it over.</p>
              </div>
              <span className="hp-suggest-cta" aria-hidden="true">Suggest an idea →</span>
            </button>
          </section>

          <AdSense slot={ADSENSE_SLOTS.homeFooter} />

          {/* ── PARTNERSHIPS ─────────────────────────────────────── */}
          <section className="hp-partnership" aria-labelledby="hp-partnership-title">
            <div className="hp-partnership-inner">
              <div className="hp-partnership-copy">
                <span className="hp-partnership-mark" aria-hidden="true">
                  <NavIcon id="share" size={21} />
                </span>
                <div>
                  <h2 className="hp-partnership-title" id="hp-partnership-title">Partner with Squadverse</h2>
                  <p>
                    We’re open to relevant advertising, sponsored challenges and collaborations with football brands,
                    creators, podcasts, kit retailers and fan communities.
                  </p>
                  <span className="hp-partnership-fit">
                    Tell us what you have in mind and we’ll see if it fits the Squadverse audience.
                  </span>
                </div>
              </div>
              <a
                href={PARTNERSHIP_MAILTO}
                className="hp-partnership-cta"
                onClick={() => trackEvent('partnership_cta_click', { placement: 'homepage_partnership_panel' })}
              >
                <span>Email about a partnership</span>
                <span aria-hidden="true">→</span>
              </a>
            </div>
            <p className="hp-partnership-email">
              Prefer to write directly? <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </p>
          </section>

        </main>

        {/* ── FOOTER ───────────────────────────────────────────────── */}
        <footer className="hp-footer">
          <div className="hp-footer-inner">
            <div className="hp-footer-top">
              <Link to="/" className="hp-footer-logo" aria-label="Squadverse">
                <img
                  src={process.env.PUBLIC_URL + '/squadverse.png'}
                  alt=""
                  className="hp-footer-logo-img"
                />
                <div>
                  <div className="hp-footer-brand">Squadverse</div>
                  <div className="hp-footer-tagline">Football games built for the group chat.</div>
                </div>
              </Link>

              <nav className="hp-footer-nav" aria-label="Footer navigation">
                <div className="hp-footer-column">
                  <span className="hp-footer-column-title">Featured</span>
                  <ul className="hp-footer-links" role="list">
                    <li><Link to="/38-0" className="hp-footer-link">38-0 Draft</Link></li>
                    <li><Link to="/transfer-window-challenge" className="hp-footer-link">Transfer Window Challenge</Link></li>
                    <li><Link to="/world-xi-cup" className="hp-footer-link">World XI Cup</Link></li>
                  </ul>
                </div>
                <div className="hp-footer-column">
                  <span className="hp-footer-column-title">Play</span>
                  <ul className="hp-footer-links" role="list">
                    <li><Link to="/38-0" className="hp-footer-link">38-0 Draft</Link></li>
                    <li><Link to="/champions-xi" className="hp-footer-link">Champions XI</Link></li>
                    <li><Link to="/card-clash" className="hp-footer-link">Card Clash</Link></li>
                    <li><Link to="/squad-builder" className="hp-footer-link">Squad Builder Challenge</Link></li>
                  </ul>
                </div>
                <div className="hp-footer-column">
                  <span className="hp-footer-column-title">Build &amp; Rank</span>
                  <ul className="hp-footer-links" role="list">
                    <li><Link to="/transfer-builder-setup" className="hp-footer-link">Transfer Builder</Link></li>
                    <li><Link to="/lineup-builder" className="hp-footer-link">Lineup Builder</Link></li>
                    <li><Link to="/buy-keep-sell" className="hp-footer-link">Buy / Keep / Sell</Link></li>
                    <li><Link to="/tier-list" className="hp-footer-link">Tier List Maker</Link></li>
                  </ul>
                </div>
                <div className="hp-footer-column">
                  <span className="hp-footer-column-title">More</span>
                  <ul className="hp-footer-links" role="list">
                    <li><a href="mailto:squadverse2025@gmail.com?subject=Squadverse bug report" className="hp-footer-link">Report Bug</a></li>
                    <li><a
                      href={PARTNERSHIP_MAILTO}
                      className="hp-footer-link"
                      onClick={() => trackEvent('partnership_cta_click', { placement: 'homepage_footer' })}
                    >Partnerships</a></li>
                    <li><a href="mailto:squadverse2025@gmail.com" className="hp-footer-link">Contact</a></li>
                    <li><Link to="/about" className="hp-footer-link">About</Link></li>
                    <li><a href="https://youtu.be/7d16CpWp-ok?si=sBTLHEIArpM35fjo" target="_blank" rel="noopener noreferrer" className="hp-footer-link">Life beyond football</a></li>
                    <li><a href="https://www.osflondon.org.uk/" target="_blank" rel="noopener noreferrer" className="hp-footer-link">Sponsor an orphan</a></li>
                    <li><Link to="/terms-and-conditions" className="hp-footer-link">Terms</Link></li>
                    <li><Link to="/privacy" className="hp-footer-link">Privacy</Link></li>
                  </ul>
                </div>
              </nav>
            </div>

            <div className="hp-footer-bottom">
              <p className="hp-footer-copy">© {new Date().getFullYear()} Squadverse. All rights reserved.</p>
              <p className="hp-footer-credit">
                Website by{' '}
                <a href="https://abuyahya.co.uk" target="_blank" rel="noopener noreferrer">
                  Abu Yahya
                </a>
              </p>
              <p className="hp-footer-disclaimer">
                Independent fan-made football tools. Not affiliated with any club, league or official body.
              </p>
            </div>
          </div>
        </footer>

        {contactModalOpen && (
          <div
            className="hp-contact-modal-backdrop"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setContactModalOpen(false);
              }
            }}
          >
            <form
              className="hp-contact-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="hp-contact-title"
              onSubmit={sendContactEmail}
            >
              <div className="hp-contact-modal-header">
                <div>
                  <span className="hp-contact-eyebrow">Contact Squadverse</span>
                  <h2 id="hp-contact-title" className="hp-contact-title">Suggest a tool</h2>
                </div>
                <button
                  type="button"
                  className="hp-contact-close"
                  aria-label="Close contact form"
                  onClick={() => setContactModalOpen(false)}
                >
                  x
                </button>
              </div>

              <label className="hp-contact-field">
                <span>Your name</span>
                <input
                  name="name"
                  value={contactForm.name}
                  onChange={updateContactForm}
                  placeholder="Optional"
                />
              </label>

              <label className="hp-contact-field">
                <span>Your email</span>
                <input
                  type="email"
                  name="email"
                  value={contactForm.email}
                  onChange={updateContactForm}
                  placeholder="Optional, for replies"
                />
              </label>

              <label className="hp-contact-field">
                <span>Subject</span>
                <input
                  name="topic"
                  value={contactForm.topic}
                  onChange={updateContactForm}
                  required
                />
              </label>

              <label className="hp-contact-field">
                <span>Message</span>
                <textarea
                  name="message"
                  value={contactForm.message}
                  onChange={updateContactForm}
                  placeholder="Tell me the tool, game mode, bug, or football idea..."
                  rows={5}
                  required
                />
              </label>

              <div className="hp-contact-actions">
                <button type="button" className="hp-contact-secondary" onClick={() => setContactModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="hp-contact-submit">
                  Send email
                </button>
              </div>
            </form>
          </div>
        )}

      </div>
    </>
  );
}

export default HomePage;
